import React, { useMemo, useEffect } from "react";
import { Typography } from "antd";
import { useLocation } from "react-router-dom";
import SbTable from "uiCore/SbTable/SbTable";
import ScaleDetailsModal from "Pages/SkillsBridgerV2/EmployeeSkillsBridger/components/ScaleDetailsModal";
import GroupPageHeader from "Pages/SkillsBridgerV2/HrSkillsBridger/shared/GroupPageHeader";
import { buildEmployeeHeaderSubtitle } from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/EmployeeSkillsMapV2.constants";
import { ComingSoonStyled } from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/SbSkillsMapV2.styles";
import {
  ProficiencyContainerStyled,
  TableAreaStyled,
  GroupTableWrapperStyled,
} from "./GroupSkillsProficiencyV2.styles";
import useGroupSkillsProficiency from "./useGroupSkillsProficiency";
import useSkillsTableColumns from "./useSkillsTableColumns";
import ScaleProficiencyFooter from "./components/ScaleProficiencyFooter";
import SkillsPaginationFooter from "./components/SkillsPaginationFooter";
import SkillsProficiencyModals from "./components/SkillsProficiencyModals";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  TEXT,
  TAB_KEYS,
  COMING_SOON_TABS,
  SKILLS_BRIDGER_DROPDOWN_ITEMS,
} from "./GroupSkillsProficiencyV2.constants";

const { Text } = Typography;

const ANALYTICS_FROM = "skills_bridger_group_proficiency";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.SKILLS_BRIDGER.GROUP_PROFICIENCY_VIEWED;

const GroupSkillsProficiencyV2 = ({ viewType = "group" }) => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const { state } = useLocation();

  // Employee view: navigated from EmployeeSkillsMapV2 with employeeSkillAnalysis.
  // Group view:    navigated from SkillsBridgerV2 with groupSkillAnalysis + selectedEmployees.
  const employee              = state?.employee              ?? {};
  const selectedEmployees     = state?.selectedEmployees     ?? [];
  const employeeSkillAnalysis = state?.employeeSkillAnalysis ?? null;
  const groupSkillAnalysis    = state?.groupSkillAnalysis    ?? null;
  const groupSummary          = state?.groupSummary          ?? null;
  const employeeInsights      = state?.employeeInsights      ?? null;
  const preloadedSkillAnalysis = viewType === "employee" ? employeeSkillAnalysis : groupSkillAnalysis;

  const {
    skills,
    paginatedSkills,
    scaleData,
    loading,
    activeTab,
    showWarning,
    showConditionalAlert,
    viewScaleModal,
    currentPage,
    pageSize,
    handleBack,
    handleTabChange,
    handleNavigateSkillsMap,
    handleProficiencyChange,
    handleWarningOk,
    handleWarningCancel,
    setShowConditionalAlert,
    setViewScaleModal,
    togglePriority,
    handlePaginationChange,
  } = useGroupSkillsProficiency({
    viewType,
    employee,
    selectedEmployees,
    preloadedSkillAnalysis,
    groupSummary,
    employeeInsights,
  });

  const columns = useSkillsTableColumns({
    onProficiencyChange: handleProficiencyChange,
    onTogglePriority: togglePriority,
    viewType,
  });

  const rightDropdownItems = useMemo(
    () => [
      { key: "skills-map", label: TEXT.TAB_SKILLS_MAP, onClick: handleNavigateSkillsMap },
    ],
    [handleNavigateSkillsMap]
  );

  const isEmployeeView = viewType === "employee";
  const pageTitle = isEmployeeView ? "Employee -" : undefined;
  const pageSubtitle = isEmployeeView ? buildEmployeeHeaderSubtitle(employee) : undefined;

  return (
    <ProficiencyContainerStyled vertical>

      <GroupPageHeader
        activeTab={activeTab}
        onBack={handleBack}
        onTabChange={handleTabChange}
        skillsBridgerDropdownItems={SKILLS_BRIDGER_DROPDOWN_ITEMS}
        rightDropdownLabel={TEXT.TAB_SKILLS_PROFICIENCY}
        rightDropdownItems={rightDropdownItems}
        pageTitle={pageTitle}
        pageSubtitle={pageSubtitle}
      />

      {activeTab === TAB_KEYS.SKILLS_BRIDGER && (
        <>
          <TableAreaStyled flex="1">
            <GroupTableWrapperStyled vertical>
              <SbTable
                columns={columns}
                dataSource={paginatedSkills}
                rowKey="skill_id"
                loading={loading}
                sticky
                pagination={false}
              />
            </GroupTableWrapperStyled>
          </TableAreaStyled>

          <ScaleProficiencyFooter
            scaleData={scaleData}
            onViewDetails={() => setViewScaleModal(true)}
          />

          <SkillsPaginationFooter
            currentPage={currentPage}
            pageSize={pageSize}
            total={skills.length}
            onChange={handlePaginationChange}
          />
        </>
      )}

      {COMING_SOON_TABS.has(activeTab) && (
        <ComingSoonStyled align="center" justify="center">
          <Text type="secondary">Coming Soon..</Text>
        </ComingSoonStyled>
      )}

      <SkillsProficiencyModals
        showWarning={showWarning}
        onWarningOk={handleWarningOk}
        onWarningCancel={handleWarningCancel}
        showConditionalAlert={showConditionalAlert}
        onConditionalAlertClose={() => setShowConditionalAlert(false)}
      />

      <ScaleDetailsModal
        open={viewScaleModal}
        scaleData={scaleData}
        onClose={() => setViewScaleModal(false)}
      />

    </ProficiencyContainerStyled>
  );
};

export default GroupSkillsProficiencyV2;
